import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button, { type IButtonProps } from '@orn/shared-web/components/Button/Button';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import PlaidConnectionModal from '../PlaidConnectionModal';
import { useAccountLink } from '../useAccountLink';

const getErrorMessage = (errorCode: ApiErrorCode | null | undefined) => {
  if (!errorCode) {
    return null;
  }

  switch (errorCode) {
    case ApiErrorCode.DUPLICATE_ITEM:
      return 'You already have an connection with this institution. Please select a different one.';
    case ApiErrorCode.PLAID_LINK_TOKEN_FAILED:
    case ApiErrorCode.PLAID_EXCHANGE_FAILED:
    default:
      return 'An error occurred while connecting your account. Please try again or contact support if the issue persists.';
  }
};

interface IPlaidLinkButtonProps extends IButtonProps, React.PropsWithChildren {
  onSuccess?: () => void;
  /**
   * Used to skip the bank connection selection modal and go directly to Plaid reconnection.
   * If null, Plaid will create a new bank connection.
   * If non-null and non-undefined, the button will skip the modal and go directly to Plaid reconnection.
   * If undefined, the button will open the modal to select a bank connection.
   */
  bankConnectionId?: string | null;
}

/**
 * Button that launches the Plaid account-linking flow. Clicking it opens the
 * connection-selection modal (or jumps straight to Plaid for a reconnection
 * when `bankConnectionId` is supplied); the modal → Plaid → exchange state
 * machine lives in {@link useAccountLink}, shared with the assistant's
 * `financial_account_connect` flow. When the flow skips the modal, link errors
 * surface inline above the button rather than in the modal.
 */
const PlaidLinkButton = ({
  onSuccess,
  children,
  className,
  bankConnectionId,
  ...buttonProps
}: IPlaidLinkButtonProps) => {
  const { trigger, shouldSkipModal, isModalOpen, onModalOpenChange, onModalSelect, errorCode, isLoading } =
    useAccountLink({ bankConnectionId, onLinked: () => onSuccess?.() });

  return (
    <>
      <div className="flex flex-col items-center">
        {shouldSkipModal && errorCode && (
          <div className="bg-error-secondary text-error-primary mb-6 rounded-lg px-3 py-2 text-sm">
            {getErrorMessage(errorCode)}
          </div>
        )}
        <Button
          className={cx('w-fit', className)}
          onClick={trigger}
          isLoading={isLoading}
          showTextWhileLoading
          size="sm"
          color="primary"
          {...buttonProps}
        >
          {children}
        </Button>
      </div>
      <PlaidConnectionModal
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        onSelect={onModalSelect}
        errorCode={errorCode}
      />
    </>
  );
};

export default PlaidLinkButton;
